"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { createClient } from "@/lib/supabase/client";
import type { User, SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@scoreach/db";

interface AuthContextValue {
  supabase: SupabaseClient<Database> | null;
  user: User | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextValue>({
  supabase: null,
  user: null,
  loading: true,
});

/**
 * Access the Supabase browser client and current user.
 * Must be used within <AuthProvider>.
 *
 * `supabase` is null only during SSR / first render before the
 * client is created. Components that need the client should
 * guard on `loading` or check `supabase` before using it.
 */
export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Provides a Supabase browser client and tracks auth state.
 * Wrap the root layout with this provider so all client
 * components can access the client via useAuth().
 *
 * The client is created lazily on first browser render to avoid
 * errors during build-time static generation when env vars are absent.
 *
 * Uses useState (not useRef) so that context consumers re-render
 * once the client is created on first mount.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [supabase, setSupabase] = useState<SupabaseClient<Database> | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const client = createClient();
    setSupabase(client);
    // MAR-202 (review 5321 #2): guard setState calls against unmount. If the
    // provider unmounts before getSession resolves (fast nav / HMR), React
    // would warn about setState on an unmounted component. Cheaper than the
    // AbortController+fetchId pattern used elsewhere — one boolean is enough
    // because there is no in-flight fetch we can abort.
    let mounted = true;

    // MAR-202: use getSession() (not getUser()) for the initial load and
    // push the JWT to the Realtime transport BEFORE flipping loading=false.
    // Two paths previously raced: getUser→setLoading(false) and
    // onAuthStateChange→setAuth. If getUser resolved first (no guarantee of
    // ordering), consumers observed loading=false with the realtime
    // transport still authenticated as anon — channels mounted at that
    // moment had RLS evaluate against the publishable key and silently
    // received zero events. Doing setAuth synchronously inside the same
    // .then() callback closes the window: any consumer that gates on
    // !loading is guaranteed to see a Realtime transport already carrying
    // the user's access token.
    client.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (!mounted) return;
        if (session?.access_token) {
          client.realtime.setAuth(session.access_token);
        }
        setUser(session?.user ?? null);
        setLoading(false);
      })
      .catch((err) => {
        // MAR-202 (review 5321 #1): without a catch, a rejected getSession
        // (localStorage corruption, network hiccup, Supabase outage) would
        // leave loading=true forever and the UI stuck on a spinner. Fall
        // through to the signed-out state so the app at least renders the
        // login screen.
        // (review 5323 #6) check mounted first — no point logging or
        // updating state for a provider that is already gone.
        if (!mounted) return;
        console.error("[auth-context] Failed to load session", err);
        setUser(null);
        setLoading(false);
      });

    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setUser(session?.user ?? null);
      // Subsequent token refreshes / sign-ins / sign-outs still go through
      // here so the transport stays in sync with the auth state.
      if (session?.access_token) {
        client.realtime.setAuth(session.access_token);
      } else {
        // MAR-202 (review 5323 #4): on SIGNED_OUT (or any session=null
        // event), clear the realtime transport's auth. Without this, a
        // still-mounted realtime subscription would continue to be
        // evaluated by RLS as the previous user — a data leak across
        // sign-out boundaries.
        client.realtime.setAuth(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext value={{ supabase, user, loading }}>
      {children}
    </AuthContext>
  );
}
